import React, { useState } from 'react';
import { CheckIcon, ChevronRightIcon } from 'lucide-react';
import { GOVERNANCE_CHAIN, GOVERNANCE_MODULES, type ChainNature } from '../../../data/customBuilder';
import { SectionHeading } from '../../ui/SectionHeading';
import { Reveal } from '../../ui/Reveal';
import { cn } from '../../../src/lib/utils';
const NATURE: Record<ChainNature, {
  label: string;
  tone: string;
}> = {
  probabilistic: {
    label: 'Probabilistic',
    tone: 'bg-[#3A2E14] text-[#F5C76B]'
  },
  evidence: {
    label: 'Evidence',
    tone: 'bg-[#16352E] text-[#7FE0C2]'
  },
  deterministic: {
    label: 'Deterministic',
    tone: 'bg-violet/20 text-violet-ink'
  },
  human: {
    label: 'Human',
    tone: 'bg-[#3A1E2B] text-[#F7A8C4]'
  },
  record: {
    label: 'Record',
    tone: 'bg-night-raised text-night-muted'
  }
};
export function GovernanceSection() {
  const [active, setActive] = useState(0);
  const step = GOVERNANCE_CHAIN[active];
  return <section aria-labelledby="governance-heading" className="dgpt-dark border-b border-night-line bg-night" data-id="element-360" data-mp="2bffpjrn9vkp7">
      <div className="mx-auto w-full max-w-[1320px] px-4 py-20 sm:px-6 lg:px-8 lg:py-28" data-id="element-361" data-mp="2cs3ml7urzrwq">
        <SectionHeading tone="dark" eyebrow="Regulated enterprise workflows" headingId="governance-heading" title="Built for AI Innovation. Designed for Institutional Control." description="Organizations need more than AI-generated answers. They need consistent processes, traceable evidence, approved evaluation criteria, and accountable decision-making. DGPT Custom Workflow Builder connects modern AI capabilities with the governance required for institutional decision-making." data-id="element-362" data-mp="2hk4tsfd36sfw" />

        <Reveal className="mt-12" data-id="element-363" data-mp="2mipyaqzdfya5">
          <ol className="grid gap-2 md:grid-cols-6" aria-label="From AI finding to decision record" data-id="element-364" data-mp="2rhspp8mfi5zn">
            {GOVERNANCE_CHAIN.map((item, index) => {
            const nature = NATURE[item.nature];
            const selected = index === active;
            return <li key={item.label} className="relative" data-id="element-365" data-mp="1g0j8ytuops21" data-mpi={index}>
                  <button type="button" aria-pressed={selected} onClick={() => setActive(index)} className={cn('flex h-full w-full flex-col items-start rounded-xl border p-3.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet', selected ? 'border-violet bg-violet/10' : 'border-night-line bg-night-panel hover:border-violet/50')} data-id="element-366" data-mp="vzhovhd0f3vg">
                    <span className="font-mono text-[11px] text-night-faint" data-id="element-367" data-mp="49llnmseo8b8">{String(index + 1).padStart(2, '0')}</span>
                    <span className="mt-1 text-sm font-semibold text-night-text" data-id="element-368" data-mp="3vgfvoa538n4g">{item.label}</span>
                    <span className={cn('mt-2 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em]', nature.tone)} data-id="element-369" data-mp="1yo603hq4v7m6">{nature.label}</span>
                  </button>
                  {index < GOVERNANCE_CHAIN.length - 1 ? <ChevronRightIcon className="absolute -right-2.5 top-1/2 z-10 hidden h-4 w-4 -translate-y-1/2 text-night-faint md:block" aria-hidden="true" data-id="element-370" data-mp="217smwawx8dpr" /> : null}
                </li>;
          })}
          </ol>

          <div className="mt-4 grid gap-4 rounded-2xl border border-night-line bg-night-panel p-6 md:grid-cols-2" aria-live="polite" data-id="element-371" data-mp="2fxgmn7pgf4bd">
            <div data-id="element-372" data-mp="1iardmdwc3rg0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-night-faint" data-id="element-373" data-mp="1ibaya2ddctxq">Example</p>
              <p className="mt-1.5 font-mono text-[13px] leading-relaxed text-night-text" data-id="element-374" data-mp="1na4c5vwkimyf">{step.example}</p>
            </div>
            <div data-id="element-375" data-mp="1w8poh9bg55qr">
              <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-night-faint" data-id="element-376" data-mp="1w97tovg36uat">What this step means</p>
              <p className="mt-1.5 text-sm leading-relaxed text-night-muted" data-id="element-377" data-mp="33lzvbocf1j3z">{step.explanation}</p>
            </div>
          </div>
          <p className="mt-4 text-[13px] leading-relaxed text-night-muted" data-id="element-378" data-mp="37pzbg5tsafwt">
            Rules and approvals can be deterministic: the same inputs always give the same result. Model-based analysis stays probabilistic and is verified against evidence before anyone relies on it. Configuring frameworks supports your compliance work; it doesn’t make a workflow automatically compliant or certified.
          </p>
        </Reveal>

        <ul className="mt-12 grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-id="element-379" data-mp="3p5biosq3zr30">
          {GOVERNANCE_MODULES.map(module => <li key={module} className="flex items-center gap-3 rounded-xl border border-night-line bg-night-panel px-4 py-3.5 text-sm text-night-text" data-id="element-380" data-mp="3k5p9cudeiuzh">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-violet/20 text-violet-ink" data-id="element-381" data-mp="3twjsvqz9jgh8">
                <CheckIcon className="h-3 w-3" strokeWidth={2.5} aria-hidden="true" data-id="element-382" data-mp="bqf5bgpybfp2" />
              </span>
              {module}
            </li>)}
        </ul>
      </div>
    </section>;
}